import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, RefreshCw } from "lucide-react";
import { isPast } from "date-fns";
import CallCard from "../components/CallCard";
import EmptyState from "../components/EmptyState";
import LoadingSkeleton from "../components/LoadingSkeleton";
import Button from "../components/Button";
import type { Call } from "../types";

export default function CallsPage() {
  const navigate = useNavigate();
  const [calls, setCalls] = useState<Call[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadCalls();
  }, []);

  const loadCalls = async () => {
    try {
      const response = await fetch("/api/calls");
      if (response.ok) {
        const data = await response.json();
        setCalls(
          data.sort(
            (a: Call, b: Call) =>
              new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime()
          )
        );
      }
    } catch (error) {
      console.error("Error loading calls:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const upcomingCalls = calls.filter(
    (c) => !isPast(new Date(c.scheduledAt)) && c.status !== "completed"
  );
  const pastCalls = calls.filter(
    (c) => isPast(new Date(c.scheduledAt)) || c.status === "completed"
  );

  if (isLoading) {
    return <LoadingSkeleton count={4} />;
  }

  if (calls.length === 0) {
    return (
      <EmptyState
        icon="calendar"
        title="No upcoming calls"
        description="Schedule your first sales call to start preparing with AI-powered insights."
        actionLabel="Schedule Your First Call"
        onAction={() => navigate("/calls/new")}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-slate-900">Calls</h2>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={loadCalls}
            icon={<RefreshCw className="w-4 h-4" />}
          >
            Refresh
          </Button>
          <Button onClick={() => navigate("/calls/new")} icon={<Plus className="w-4 h-4" />}>
            New Call
          </Button>
        </div>
      </div>

      {upcomingCalls.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
            Upcoming
          </h3>
          <div className="space-y-3">
            {upcomingCalls.map((call) => (
              <CallCard
                key={call.id}
                call={call}
                onClick={() => navigate(`/calls/${call.id}`)}
              />
            ))}
          </div>
        </div>
      )}

      {pastCalls.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
            Past
          </h3>
          <div className="space-y-3">
            {pastCalls.map((call) => (
              <CallCard
                key={call.id}
                call={call}
                onClick={() => navigate(`/calls/${call.id}`)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
